@import '@/theme/media.css';

/* Home, Community impact and the neighbour profile (mobile/src/screens/home/*Screen.tsx styles). */
.page {
  width: 100%;
  max-width: var(--content-max);
  margin-inline: auto;
  padding: var(--space-4) var(--space-6) var(--space-8);
}

/* Desktop: the welcome column beside the nearby feed. */
@media (--wide) {
  .page {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1.2fr);
    gap: var(--space-8);
    align-items: start;
    max-width: 72rem;
  }

  .feed > h2:first-child {
    margin-top: var(--space-2);
  }
}

.narrow {
  width: 100%;
  max-width: var(--content-max);
  margin-inline: auto;
  padding: 0 var(--space-6) var(--space-8);
}

.grow {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
}

.greeting {
  margin: 0 0 var(--space-1);
  font-size: var(--text-2xl);
}

.subtitle {
  margin: 0 0 var(--space-5);
  color: var(--text-2);
}

.ctaRow {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-3);
}

.cta {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: var(--space-4);
  min-height: 7.5rem;
  padding: var(--space-5) var(--space-4);
  text-decoration: none;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-card);
}

.cta[data-kind='need'] {
  color: var(--on-primary);
  background: var(--primary-fill);
}

.cta[data-kind='offer'] {
  color: var(--on-secondary);
  background: var(--secondary);
}

.cta:hover {
  box-shadow: var(--shadow-raised);
}

.ctaText {
  font-family: var(--font-heading);
  font-size: var(--text-lg);
  font-weight: var(--weight-extrabold);
}

.card {
  background: var(--surface);
  border: var(--border-width) solid var(--border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-card);
}

.impact {
  composes: card;
  display: flex;
  align-items: center;
  gap: var(--space-3);
  margin-top: var(--space-4);
  padding: var(--space-4);
  text-decoration: none;
  color: var(--text);
  background: var(--success-soft);
  border-color: var(--success);
}

/* AA contrast on the green card (--text-2 is 4.1:1 on --success-soft). */
.impact .rowMeta {
  color: var(--text-2-strong);
}

.impactIcon {
  display: grid;
  place-items: center;
  width: 2.5rem;
  height: 2.5rem;
  color: var(--success-strong);
  background: var(--surface);
  border-radius: var(--radius-pill);
}

.rowTitle {
  font-weight: var(--weight-bold);
  color: var(--text);
}

.rowMeta {
  margin-top: var(--space-1);
  font-size: var(--text-sm);
  color: var(--text-2);
}

.spotlight {
  composes: card;
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-4);
  text-decoration: none;
  color: inherit;
}

.nameRow {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
}

.blurb {
  margin-top: var(--space-1);
  font-size: var(--text-sm);
  line-height: var(--leading-normal);
  color: var(--text-2);
}

.chips {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  margin-bottom: var(--space-3);
}

.feedList {
  composes: card;
  margin: 0 0 var(--space-3);
  padding: var(--space-1) var(--space-4);
  list-style: none;
}

.feedList > li + li {
  border-top: var(--border-width) solid var(--border);
}

.feedRow {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  min-height: var(--tap-min);
  padding: var(--space-4) var(--space-1);
  text-decoration: none;
  color: var(--text-3);
}

.feedRow:hover .rowTitle {
  text-decoration: underline;
}

.empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-8) var(--space-5);
  text-align: center;
  color: var(--text-2);
}

.seeAll {
  display: block;
  padding: var(--space-3);
  font-family: var(--font-heading);
  font-weight: var(--weight-bold);
  text-align: center;
  color: var(--primary-strong);
}

.footerRow {
  display: grid;

  /* Two tiles side by side, stacked when large text needs the room (WEB-025). */
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 9rem), 1fr));
  gap: var(--space-3);
  margin-top: var(--space-5);
}

.footerTile {
  composes: card;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-2);
  padding: var(--space-4);
  text-decoration: none;
  color: inherit;
}

.footerIcon[data-tone='primary'] {
  color: var(--primary-strong);
}

.footerIcon[data-tone='secondary'] {
  color: var(--secondary-text-strong);
}

.footerSubtitle {
  font-size: var(--text-xs);
  color: var(--text-2);
}

.countBadge {
  position: absolute;
  top: calc(var(--space-2) * -1);
  right: calc(var(--space-2) * -1);
  display: grid;
  place-items: center;
  min-width: 1.25rem;
  height: 1.25rem;
  padding: 0 var(--space-1);
  font-family: var(--font-heading);
  font-size: var(--text-xs);
  font-weight: var(--weight-extrabold);
  color: var(--on-primary);
  background: var(--primary-fill);
  border: var(--border-width-strong) solid var(--bg);
  border-radius: var(--radius-pill);
}

/* Community impact */
.hero {
  composes: card;
  padding: var(--space-6) var(--space-5);
  text-align: center;
  background: var(--success-soft);
  border-color: var(--success);
}

.heroNumber {
  display: block;
  font-family: var(--font-heading);
  font-size: var(--text-3xl);
  font-weight: var(--weight-extrabold);
  color: var(--success-strong);
}

.heroLabel {
  margin: var(--space-1) 0 0;
  color: var(--text);
}

.tiles {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--space-3);
  margin: var(--space-4) 0 0;
  padding: 0;
  list-style: none;
}

@media (--wide) {
  .tiles {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

.tile {
  composes: card;
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  padding: var(--space-4);
}

.tileValue {
  font-family: var(--font-heading);
  font-size: var(--text-2xl);
  font-weight: var(--weight-extrabold);
  color: var(--text);
}

.tileValue[data-tone='primary'] {
  color: var(--primary-strong);
}

.tileLabel {
  font-size: var(--text-sm);
  color: var(--text-2);
}

.chart {
  composes: card;
  margin: var(--space-4) 0 0;
  padding: var(--space-5);
}

.chartHeader {
  display: flex;
  justify-content: space-between;
  gap: var(--space-2);
  margin-bottom: var(--space-4);
  font-weight: var(--weight-bold);
}

.chartPeak {
  font-size: var(--text-sm);
  font-weight: var(--weight-regular);
  color: var(--text-2);
}

.bars {
  display: flex;
  align-items: flex-end;
  gap: var(--space-2);
  height: 8rem;
}

.barColumn {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  gap: var(--space-1);
  height: 100%;
}

.bar {
  width: 100%;
  min-height: var(--space-1);
  background: var(--primary);
  border-radius: var(--radius-sm) var(--radius-sm) 0 0;
}

.barDay {
  font-size: var(--text-xs);
  color: var(--text-2);
}

.summary {
  margin: var(--space-5) 0 0;
  line-height: var(--leading-normal);
  color: var(--text-2);
}

/* Neighbour profile */
.profileHeader {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-4) 0;
  text-align: center;
}

.profileName {
  margin: 0;
  font-size: var(--text-2xl);
}

.pillList {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  margin: 0;
  padding: 0;
  list-style: none;
}

.pill {
  padding: var(--space-1) var(--space-3);
  font-size: var(--text-sm);
  font-weight: var(--weight-semibold);
  background: var(--surface-2);
  border-radius: var(--radius-pill);
}

.plainList {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  margin: 0;
  padding: 0;
  list-style: none;
}

.noteCard {
  composes: card;
  padding: var(--space-4);
}

.noteText {
  margin: 0;
  font-style: italic;
  line-height: var(--leading-normal);
}

.noteFrom {
  margin: var(--space-2) 0 0;
  font-size: var(--text-sm);
  color: var(--text-2);
}

.itemCard {
  composes: card;
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  padding: var(--space-4);
  text-decoration: none;
  color: inherit;
}

.itemMeta {
  font-size: var(--text-sm);
  color: var(--text-2);
}

/* Community updates */
.story {
  composes: card;
  display: flex;
  gap: var(--space-3);
  padding: var(--space-4);
}

.storyText {
  margin: var(--space-1) 0;
  line-height: var(--leading-normal);
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.storyComposer {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  margin-top: var(--space-5);
}

.storyError {
  margin: 0;
  font-weight: var(--weight-semibold);
  color: var(--danger-text);
}

/* specs/003 #4: a paid-for promotion is labelled and tinted, so it is never mistaken for a neighbour's post. */
.progress {
  display: flex;
  margin-bottom: var(--space-1);
}

.feedRow[data-sponsored] {
  padding-inline: var(--space-3);
  border-radius: var(--radius-md);
  background: var(--secondary-soft);
}

.feedRow[data-sponsored] .rowMeta {
  color: var(--text-2-strong); /* --text-2 on the tint is 4.19:1; this is 4.72:1 */
}

.sponsored {
  display: inline-block;
  align-self: flex-start; /* a label, not a bar across the row */
  margin-bottom: var(--space-1);
  padding: 0 var(--space-2);
  border-radius: var(--radius-pill);
  background: var(--surface);
  color: var(--secondary-text);
  font-size: var(--text-sm);
  font-weight: 700;
}

/* Community updates: photos, hearts, the story bar on Home and the viewer */
.storyPhoto {
  max-width: 100%;
  max-height: 16rem;
  margin: var(--space-2) 0;
  border-radius: var(--radius-lg);
  object-fit: cover;
}

.storyActions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  align-items: center;
  margin-top: var(--space-2);
}

.heartButton {
  min-height: var(--tap-min);
  padding: 0 var(--space-4);
  font: inherit;
  font-weight: var(--weight-semibold);
  color: var(--text);
  cursor: pointer;
  background: var(--surface);
  border: var(--border-width) solid var(--border);
  border-radius: var(--radius-pill);
}

.heartButton[aria-pressed='true'] {
  color: var(--danger-text);
  border-color: var(--danger-text);
}

.heartButton:focus-visible {
  outline: var(--focus-ring);
  outline-offset: var(--focus-ring-offset);
}

.storyBar {
  margin: 0 0 var(--space-4);
}

.storyBarList {
  display: flex;
  gap: var(--space-4);
  margin: 0;
  padding: var(--space-1) 0;
  overflow-x: auto;
  list-style: none;
}

.storyBarItem {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-1);
  width: 4.5rem;
  color: inherit;
  text-decoration: none;
}

.storyRing {
  position: relative;
  display: inline-flex;
  padding: var(--space-1);
  border: 2px solid var(--border);
  border-radius: var(--radius-pill);
}

.storyRing[data-unseen] {
  border-color: var(--primary-strong);
}

.storyRing[data-own] {
  border-style: dashed;
}

.storyPlus {
  position: absolute;
  right: -2px;
  bottom: -2px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  height: 1.25rem;
  color: var(--on-primary);
  background: var(--primary-strong);
  border-radius: var(--radius-pill);
}

.storyName {
  max-width: 100%;
  overflow: hidden;
  font-size: var(--text-sm);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.viewer {
  composes: card;
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-4);
}

.viewerHead {
  display: flex;
  gap: var(--space-3);
  align-items: center;
}

.viewerPhoto {
  width: 100%;
  max-height: 24rem;
  border-radius: var(--radius-lg);
  object-fit: cover;
}

.viewerNav {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-3);
}

/* Community updates: room between one update and the next, and between my own and my connections' */
.storyList {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
  margin: 0 0 var(--space-6);
  padding: 0;
  list-style: none;
}

.storyList .story {
  padding: var(--space-5);
}

/* A sponsored row is a tinted card of its own: room around it, and no divider line cutting into it. */
.feedList > li:has(> [data-sponsored]) {
  margin-block: var(--space-2);
  border-top: 0;
}

.feedList > li:has(> [data-sponsored]) + li {
  border-top: 0;
}
